import React, { useEffect, useState, useContext } from 'react';
import closeImg from './assets/images/close_1close.png';
import externalImg from './assets/images/external-link.svg';
import { AccountProvider } from './context';
import './assets/css/normalize.css';
import './assets/css/webflow.css';
import './assets/css/ibl-default-account-ui.webflow.css';
import './assets/css/custom.css';
import { Tab } from './layouts';
import Basic from './layouts/tab-contents/Basic';
import Social from './layouts/tab-contents/Social';
import Security from './layouts/tab-contents/Security';
import Header from './layouts/Header';
import { api } from '../../hooks';
import { AppDataContext } from '../../utils/context';
import { IUserData, IUserMetaData } from '../../model';
import { useDebounce } from './helpers/useDebounce';

const TABS = [
  {
    slug: 'Basic',
    name: 'Basic Information',
    layout: <Basic />,
  },
  {
    slug: 'Social',
    name: 'Social Information',
    layout: <Social />,
  },
  {
    slug: 'Security',
    name: 'Security',
    layout: <Security />,
  },
];

const STRIPE_ENABLED = process.env.REACT_APP_IBL_ENABLE_STRIPE === 'true';

export const Account = ({ onModalClose }) => {
  const [focusedTab, setFocusedTab] = useState('Basic');
  const [initialUserEmail, setInitialUserEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { appData, setAppData } = useContext(AppDataContext);
  const [userData, setUserData] = useState<IUserData>({ ...appData.userData });
  const [userMetaData, setUserMetaData] = useState<IUserMetaData>({
    ...appData.userMetaData,
  });
  const [userProfilePicUpdated, setUserProfilePicUpdated] = useState(false);
  const [stripeBillingURL, setStripeBillingURL] = useState('');

  const handleRetrieveBillingURL = () => {
    if (!STRIPE_ENABLED) {
      return;
    }
    const url = new URL(window.location.href);
    const urlOrigin = url.origin;
    const data = {
      return_url: urlOrigin,
      callback: 'createCustomerPortalURL',
    };
    stripeBilling(data, (url) => {
      if (url && String(url) !== '') {
        setStripeBillingURL(url);
      }
    });
  };

  const stripeBilling = (data, callback) => {
    api.iblwebstripe.postToStripe(data, function (url) {
      callback(url);
    });
  };

  const handleBillingClick = () => {
    if (stripeBillingURL && String(stripeBillingURL) !== '') {
      window.location.href = stripeBillingURL;
    }
  };

  useEffect(() => {
    handleUserData();
    handleRetrieveBillingURL();
  }, []);
  const METADATANAMES = ['bio', 'about', 'is_profile_public', 'name', 'email'];

  const userCanStoreProfilePic = () => {
    const actualYearOfBirth = appData?.userMetaData?.year_of_birth;
    console.log('Year of birth : ', actualYearOfBirth);
    if (!actualYearOfBirth) {
      return false;
    }
    try {
      return new Date().getFullYear() - actualYearOfBirth >= 13;
      //ACCEPTABLE AGE LIMIT FOR EDX TO STORE PICTURE PIC
    } catch {
      return false;
    }
  };

  const handleStoreProfilePic = (formData: any) => {
    const username = appData?.userMetaData?.username;
    const learnURL = process.env.REACT_APP_IBL_LMS_URL;
    fetch(`${learnURL}/api/user/v1/accounts/${username}/image`, {
      method: 'POST',
      //headers: myHeaders,
      body: formData,
      redirect: 'follow',
      credentials: 'include', // This line is added to include credentials like cookies
    })
      .then((response) => response.text())
      .then((result) => {
        fetch(`${learnURL}/api/user/v1/accounts/${username}`, {
          method: 'GET',
          credentials: 'include', // This line is added to include credentials like cookies
        })
          .then((response) => response.json())
          .then((fetchedUserData: any) => {
            const updatedMetaData = {
              ...userMetaData,
              public_metadata: {
                ...userMetaData?.public_metadata,
                profile_image: fetchedUserData?.profile_image,
              },
              profile_image: fetchedUserData?.profile_image,
            };
            setUserMetaData(updatedMetaData);
            setAppData({
              ...appData,
              userMetaData: { ...updatedMetaData },
            });
            setUserProfilePicUpdated(true);
          });

        /*api.ibledxusers.getUserAccountDetails({
            username:appData?.userMetaData?.username,
          }, (fetchedUserData)=>{
            const updatedMetaData = {
              ...userMetaData,
              public_metadata: { ...appData['public_metadata'], profile_image:fetchedUserData?.profile_image?.image_url_full },
            }
            setUserMetaData(updatedMetaData);
            setAppData({
              ...appData,
              userMetaData: { ...updatedMetaData },
            });
          })*/
      })
      .catch((error) => console.error(error));
  };

  const handleImgUploadToAPI = (formdata: any) => {
    const userCanStore = userCanStoreProfilePic();
    if (userCanStore) {
      handleStoreProfilePic(formdata);
    } else {
      handleUpdateUserDateOfBirth()
        .then((response) => {
          handleStoreProfilePic(formdata);
        })
        .catch((err) => {
          //
        });
    }
  };

  const handleUpdateUserDateOfBirth = async () => {
    const username = appData?.userMetaData?.username;
    const learnURL = process.env.REACT_APP_IBL_LMS_URL;
    const apiUrl = `${learnURL}/api/user/v1/accounts/${username}`;
    const DEFAULT_YEAR_OF_BIRTH = '1996';

    const payload = {
      year_of_birth: DEFAULT_YEAR_OF_BIRTH,
    };

    return fetch(apiUrl, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/merge-patch+json',
      },
      body: JSON.stringify(payload),
      credentials: 'include',
    }).then((response) => response.json());
  };

  const handleSubmit = () => {
    handleMetaDataSubmitToAPI();
  };

  const handleMetaDataSubmitToAPI = () => {
    const { bio, about, is_profile_public, social_links, profile_image } =
      userMetaData;
    const emailGotUpdated =
      userMetaData?.email !== initialUserEmail && userMetaData?.email !== '';
    const updatedDataWithoutProfilePic = {
      name: userMetaData?.name || '',
      bio,
      about,
      is_profile_public,
      social_links,
      ...(!!emailGotUpdated
        ? {
            email: userMetaData.email,
          }
        : {}),
    };
    const metaDataToSubmit = {
      public_metadata: {
        ...updatedDataWithoutProfilePic,
        profile_image,
      },
      ...updatedDataWithoutProfilePic,
    };
    setIsSubmitting(true);
    api.ibledxusers.postUsersManageMetadata(metaDataToSubmit, function (data) {
      setIsSubmitting(false);
      setUserProfilePicUpdated(false);
      if (data.status_code == 200) {
        setAppData({
          ...appData,
          userMetaData: { ...userMetaData },
          userData: {
            ...userData,
            user_fullname: userMetaData?.name,
            ...(!!emailGotUpdated
              ? {
                  user_email: userMetaData?.email,
                }
              : {}),
          },
        });
        setInitialUserEmail(userMetaData?.email);
      }
    });
  };

  //TRIGGERS SAVE TO METADATA WHENEVER PROFILE PIC UPDATED
  useDebounce(
    () => {
      if (userProfilePicUpdated) {
        handleMetaDataSubmitToAPI();
      }
    },
    300,
    [userProfilePicUpdated]
  );

  const handlePassWordForgotten = () => {
    const email = userData.user_email;
    api.iblwebauth.forgetPassword(
      {
        email: email,
      },
      (data) => {
        console.log('password changed');
      }
    );
  };

  const handleModalCloserClick = () => {
    onModalClose(false);
  };

  const handleUserData = () => {
    if (!userMetaData) {
      api.ibledxusers.getUsersManageMetadata(null, function (data) {
        const updatedUserMetaData = data?.body?.public_metadata;
        setUserMetaData({ ...updatedUserMetaData });
        setAppData({
          ...appData,
          ibl_user_public_metadata: { ...updatedUserMetaData },
        });
      });
    }
    setInitialUserEmail(userData?.user_email);
  };

  return (
    <AccountProvider
      value={{
        focusedTab,
        setFocusedTab,
        onUserDataChange: (_userData: IUserData) =>
          setUserData({ ..._userData }),
        userData: { ...userData },
        userMetaData: { ...userMetaData },
        onUserMetaDataChange: (_userMetaData: IUserMetaData) =>
          setUserMetaData({ ..._userMetaData }),
        METADATANAMES,
        handleSubmit,
        handlePassWordForgotten,
        handleImgUploadToAPI,
        isSubmitting,
        stripeBillingURL,
        handleBillingClick,
      }}
    >
      <div
        className={`modal manage-account-modal ${
          stripeBillingURL !== '' ? '' : 'no-billing'
        }`}
        style={{
          display: 'flex',
        }}
      >
        <div className="w-layout-vflex-account-ui modal-container export-modal-container larger">
          <div className="w-layout-vflex-account-ui modal-body manage-account-modal-body">
            <div className="w-layout-vflex-account-ui account-user-details-block">
              <Header />
              <div className="w-layout-hflex-account-ui account-seperator"></div>
              <div
                className={`w-layout-grid-account-ui account-grid-block ${
                  stripeBillingURL !== '' ? 'with-billing' : ''
                }`}
              >
                {TABS.map((tab, index) => (
                  <Tab key={index} slug={tab.slug} name={tab.name} />
                ))}
                {stripeBillingURL !== '' && (
                  <Tab
                    slug={'Billing'}
                    name={'Billing'}
                    url={stripeBillingURL}
                  />
                )}
              </div>
              <div
                onClick={() => handleModalCloserClick()}
                className="modal-close-wrapper hidden-sm"
              >
                <img src={closeImg} loading="lazy" alt="" />
              </div>
            </div>
            <div className="w-layout-hflex-account-ui account-user-form-block">
              <div className="w-layout-hflex-account-ui modal-header">
                <h3 className="modal-header-title">
                  {TABS.find((el) => el.slug === focusedTab).name}
                </h3>
                {stripeBillingURL !== '' && (
                  <div
                    onClick={() => handleBillingClick()}
                    className="w-layout-hflex-account-ui billing-btn-block"
                  >
                    <h5 className="billing-btn-header">Billing</h5>
                    <img
                      src={externalImg}
                      loading="lazy"
                      alt=""
                      className="billing-btn-icon invert"
                    />
                  </div>
                )}
                <div
                  onClick={() => handleModalCloserClick()}
                  className="modal-close-wrapper"
                >
                  <img src={closeImg} loading="lazy" alt="" />
                </div>
              </div>
              {TABS.find((el) => el.slug === focusedTab).layout}
            </div>
          </div>
        </div>
      </div>
    </AccountProvider>
  );
};
